import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ComposedChart,
} from 'recharts';

const AXIS = '#CBD5E1';
const GRID = '#E2E8F0';
const TICK = '#64748B';
const GREEN = '#10B981';
const RED = '#EF4444';
const BLUE = '#3B82F6';

const KpiCard = ({ label, value, hint }) => (
  <Paper elevation={2} sx={{ p: 2.5, flex: '1 1 160px', minWidth: 160 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>{value}</Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

const ChartCard = ({ title, description, children }) => (
  <Paper elevation={2} sx={{ p: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
    )}
    {children}
  </Paper>
);

/**
 * Whether the bug pool is working.
 *
 * Four questions, and only four, because the temptation with bug data is to report
 * everything measurable and answer nothing. Are we fixing them as fast as we find
 * them; is cool-down big enough to absorb what we decided to put there; how often
 * are we interrupting ourselves; and how old is the pile we are ignoring.
 *
 * Notably absent is anything per-person. Bugs closed per engineer is a metric that
 * changes behaviour the moment it is displayed, and not in the direction anybody
 * claims to want.
 */
const BugAnalytics = ({ data }) => {
  if (!data) {
    return <Alert severity="info">Could not load bug analytics for this workspace.</Alert>;
  }

  const { perCycle = [], ageBuckets = [], totals = {}, settings = {} } = data;
  const measured = perCycle.filter(row => row.reported > 0 || row.absorbed > 0 || row.emergencies > 0);

  const totalReported = measured.reduce((sum, row) => sum + row.reported, 0);
  const totalAbsorbed = measured.reduce((sum, row) => sum + row.absorbed, 0);
  const daysLost = measured.reduce((sum, row) => sum + (row.emergencyCostDays || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Bugs</Typography>
        <Typography variant="body2" color="text.secondary">
          Whether the pool is working. There is nothing per-person here on purpose:
          bugs closed per engineer is a number that changes behaviour as soon as it
          is on a wall, and not for the better.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <KpiCard
          label="Open"
          value={totals.open ?? 0}
          hint={`${totals.untriaged ?? 0} still waiting on a decision`}
        />
        <KpiCard
          label="Found vs fixed"
          value={totalReported ? `${totalAbsorbed}/${totalReported}` : '—'}
          hint="Across measured cycles. Below half and the pool is growing."
        />
        <KpiCard
          label="Days lost to emergencies"
          value={daysLost ? daysLost.toFixed(1) : '0'}
          hint="Charged against the bets that were interrupted"
        />
        <KpiCard
          label="Came back"
          value={totals.repeatOffenders ?? 0}
          hint="Reported more than once — the pool's own evidence of what matters"
        />
      </Box>

      {totals.fading > 0 && (
        <Alert severity="info">
          {totals.fading === 1
            ? 'One bug has faded'
            : `${totals.fading} bugs have faded`}
          {' '}after {settings.bugFadeAfterDays} days with nobody picking them up.{' '}
          <Link component={RouterLink} to="/bugs">Sweeping them</Link> is safe: anything
          real gets reported again and comes straight back.
        </Alert>
      )}

      {measured.length === 0 ? (
        <Alert severity="info">
          No bugs have been reported or fixed inside a cycle's dates yet, so there is
          no trend to read. This fills in as bugs are triaged and cool-downs absorb them.
        </Alert>
      ) : (
        <ChartCard
          title="Found against fixed, per cycle"
          description="The comparison that matters. A team finding fifteen and fixing five does not have a list problem, it has a trend, and no amount of grooming the list changes it. The line is emergencies against the budget."
        >
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={measured} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke={AXIS} tick={{ fill: TICK, fontSize: 12 }} />
              <YAxis stroke={AXIS} tick={{ fill: TICK, fontSize: 12 }} allowDecimals={false} />
              <RechartsTooltip />
              <Legend />
              {settings.emergencyBudgetPerCycle > 0 && (
                <ReferenceLine
                  y={settings.emergencyBudgetPerCycle}
                  stroke={RED}
                  strokeDasharray="4 4"
                  label={{ value: 'Emergency budget', fill: TICK, fontSize: 11, position: 'insideTopRight' }}
                />
              )}
              <Bar dataKey="reported" name="Reported" fill={BLUE} radius={[3, 3, 0, 0]} />
              <Bar dataKey="absorbed" name="Fixed in this cycle" fill={GREEN} radius={[3, 3, 0, 0]} />
              <Line
                type="monotone"
                dataKey="emergencies"
                name="Emergencies"
                stroke={RED}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <ChartCard
        title="How old the open pool is"
        description="Buckets rather than an average, because an average hides the shape: twenty fresh bugs and one from last year average out to something that describes neither."
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={ageBuckets} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
            <XAxis dataKey="label" stroke={AXIS} tick={{ fill: TICK, fontSize: 12 }} />
            <YAxis stroke={AXIS} tick={{ fill: TICK, fontSize: 12 }} allowDecimals={false} />
            <RechartsTooltip />
            <Bar dataKey="count" name="Open bugs" fill={BLUE} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </Box>
  );
};

export default BugAnalytics;
